
import { mapState, mapGetters, mapMutations } from 'vuex';
import PaginatedReviewResourceView from '@/mixins/PaginatedReviewResourceView';
import { POPUP_PER } from '@/constants/PARAMS';
import SocialMediaType from '@/enums/SocialMediaType';
import { isIframe } from '@/lib/utils';
import ReviewsShortsPopupMobile from './ReviewsShortsPopupMobile';
import ReviewsShortsPopupPc from './ReviewsShortsPopupPc';

const STORAGE_KEY = 'coach_mark_dismissed';

export default {
  i18nComponentKey: 'ReviewsShortsPopup.ReviewsShortsPopup',
  name: 'ReviewsShortsPopup',
  components: {
    ReviewsShortsPopupMobile,
    ReviewsShortsPopupPc
  },
  mixins: [PaginatedReviewResourceView],
  data() {
    return {
      isVideoMuted: true,
      showCoachMark: true,
      hasEmittedReady: false
    };
  },
  computed: {
    ...mapState('window', ['windowService']),
    ...mapState('widget', ['reviewId']),
    ...mapState('review', ['mediaIndex']),
    ...mapState('reviewSettings', ['reviewSettings']),
    ...mapGetters('widget', ['isPcViewport']),
    ...mapGetters('review', ['displayableReview']),
    ...mapGetters('reviewSettings', ['isAuthorDisplayTypeUserGrade']),
    defaultResourceParams() {
      return {
        fields: [
          'reviews.ai_summary',
          'reviews.customer_properties',
          'media_page',
          this.isAuthorDisplayTypeUserGrade ? 'reviews.brand_user_grade' : '',
          this.reviewSettings.use_comments &&
          this.reviewSettings.always_show_comments
            ? 'reviews.comments'
            : ''
        ]
          .filter(v => v)
          .join(','),
        per: POPUP_PER,
        review_id: this.reviewId,
        with_media: true
      };
    },
    reviewMedia() {
      return this.reviews.flatMap((review, index) => {
        const count =
          review.social_media_type === SocialMediaType.INSTAGRAM
            ? 1
            : review.videos.length + review.images.length;
        return Array.from({ length: count }, (_, i) => ({
          review: this.displayableReview(review),
          media_position: i + 1,
          review_index: index
        }));
      });
    },
    initialMediaIndex() {
      if (!this.reviewId) return 0;

      const reviewId = parseInt(this.reviewId);
      let mediaPosition = parseInt(this.mediaIndex);
      if (isNaN(mediaPosition)) mediaPosition = 1;

      const index = this.reviewMedia.findIndex(
        media =>
          media.review.id === reviewId && media.media_position === mediaPosition
      );
      return index >= 0 ? index : 0;
    },
    shortsProps() {
      return {
        reviewMedia: this.reviewMedia,
        initialMediaIndex: this.initialMediaIndex,
        pagy: this.pagy,
        popupParams: this.popupParams,
        isVideoMuted: this.isVideoMuted,
        showCoachMark: this.showCoachMark,
        showCloseButton: isIframe()
      };
    },
    shortsEvents() {
      return {
        'append-page': this.onAppendPage,
        'prepend-page': this.onPrependPage,
        'toggle-volume': this.toggleVolume,
        'dismiss-coach-mark': this.dismissCoachMark,
        close: this.onClose
      };
    }
  },
  created() {
    this.SET_EDIT_OR_DELETE_CALLBACK(() => this.searchResource());
    this.checkCoachMark();
  },
  beforeDestroy() {
    this.SET_EDIT_OR_DELETE_CALLBACK(null);
  },
  methods: {
    ...mapMutations('review', ['SET_EDIT_OR_DELETE_CALLBACK']),
    onContentReady() {
      if (this.hasEmittedReady) return;

      this.hasEmittedReady = true;
      this.$emit('ready');
    },
    onClose() {
      this.windowService.closeFullscreenPopup();
    },
    toggleVolume() {
      this.isVideoMuted = !this.isVideoMuted;
    },
    checkCoachMark() {
      if (localStorage.getItem(STORAGE_KEY)) {
        this.showCoachMark = false;
      } else {
        this.showCoachMark = true;
      }
    },
    onAppendPage({ page, callback }) {
      this.appendPage({ page }).then(() => {
        if (callback) callback();
      });
    },
    onPrependPage({ page, callback }) {
      const prevLength = this.reviewMedia.length;
      this.prependPage(page).then(() => {
        callback(this.reviewMedia.length - prevLength);
      });
    },
    dismissCoachMark() {
      this.showCoachMark = false;
      localStorage.setItem(STORAGE_KEY, 'true');
    }
  }
};
